import {
  Button,
  Emphasis,
  FormAction,
  FormField,
  FormGrid,
  FormNotice,
  FormRow,
  FormTitle,
} from "@lya-protect/lya-protect-form-library/dist/UI";
import {
  Form,
  InputCheckbox,
  InputText,
  useFormConfig,
} from "@lya-protect/lya-protect-form-library/dist/FormAPI";
import { zod } from "@lya-protect/lya-protect-form-library/dist/Exports";
import { InfoBulleButton } from "@src/components/infoBulleButton/InfoBulleButton";
import { InfoCard } from "@src/components/infoCard/InfoCard";
import { LoaderOverlay } from "@src/components/loaderOverlay/LoaderOverlay";
import { NavButton } from "@src/components/navButton/NavButton";
import { InfoBulle } from "@src/components/infoBulle/InfoBulle";
import { gatewayAPI } from "@src/GatewayAPI";
import { PopperButton } from "@src/PopperButton";
import { StepProps } from "../../step/Steps";

import "./addressInfos.scss";

export type AddressInfosData = {
  address: string;
  complement?: string;
  zipcode: string;
  city: string;
  mobilePhone: string;
  fixedPhone?: string;
  email: string;
  consent: boolean;
  accept: boolean;
};
type AddressInfosProps = StepProps<AddressInfosData>;

const schema = zod.object({
  address: zod
    .string({ required_error: "Champ requis" })
    .min(1, "Champ requis")
    .max(95, "Adresse trop longue"),
  complement: zod.string().optional(),
  zipcode: zod.string({ required_error: "Champ requis" }),
  city: zod
    .string({ required_error: "Champ requis" })
    .min(1, "Champ requis")
    .max(95, "Ville trop longue"),

  mobilePhone: zod
    .string({ required_error: "Champ requis" })
    .min(10, "Champ requis")
    .max(13, "Numéro trop long")
    .refine(
      (val) => {
        const cleaned = val.replace(/[\s.\-]/g, "");
        return /^(0[67]\d{8}|(?:\+33|0033)[67]\d{8})$/.test(cleaned);
      },
      { message: "Format téléphone mobile invalide" }
    ),
  fixedPhone: zod
    .string()
    .optional()
    .refine(
      (val) => {
        if (!val) return true;
        const cleaned = val.replace(/[\s.\-]/g, "");
        return /^(0[1-5,9]\d{8}|(?:\+33|0033)[1-5,9]\d{8})$/.test(cleaned);
      },
      { message: "Format téléphone fixe invalide" }
    ),
  email: zod
    .string({ required_error: "Champ requis" })
    .email()
    .min(1, "Champ requis")
    .max(95, "Email trop longue"),
  accept: zod.boolean(),
  consent: zod
    .boolean({ required_error: "Champ requis" })
    .refine((val) => val, { message: "Champ requis" }),
});
type FormValues = zod.infer<typeof schema>;

export function AddressInfos({ changeStep, data, setData }: AddressInfosProps) {
  const formConfig = useFormConfig<typeof schema, FormValues>({
    schema,
    defaultValues: data,
  });
  const zipcode = formConfig.watch("zipcode");

  return (
    <>
      <LoaderOverlay
        isLoading={
          formConfig.formState.isSubmitting ||
          formConfig.formState.isSubmitSuccessful
        }
      />
      <div className="landing-step">
        <div className="stepper__centered-container landing-step__first-part">
          <div className="stepper__centered-content step__grid ">
            <NavButton
              onClick={() => {
                changeStep("CONTACT_INFOS", "backward");
              }}
            >
              Précédent
            </NavButton>
            <Form
              className="situation-step__form"
              formConfig={formConfig}
              onSubmit={(values) => {
                setData(values);
                changeStep("RECAP", "forward");
              }}
            >
              <FormGrid>
                <FormTitle>
                  <h1>
                    Renseignez <Emphasis>vos coordonnées</Emphasis> pour
                    recevoir votre devis
                  </h1>
                </FormTitle>
                <FormNotice>
                  <Emphasis>*</Emphasis> Champs obligatoires
                </FormNotice>
                <FormRow>
                  <FormField>
                    <InputText name="address" required label="Adresse" />
                  </FormField>
                </FormRow>
                <FormRow>
                  <FormField>
                    <InputText name="complement" label="Complément d'adresse" />
                  </FormField>
                </FormRow>
                <FormRow>
                  <FormField>
                    <InputText
                      name="zipcode"
                      required
                      label="Code postal"
                      placeholder="75013"
                      autoComplete={{
                        displayLabel: (result: string) =>
                          `${zipcode || ""} - ${result}`,
                        fetchResults: async () => {
                          if (!zipcode || zipcode.length !== 5) return [];
                          return gatewayAPI.axios
                            .get<string[]>(`geo/communes/search/${zipcode}`)
                            .then((res) => res.data);
                        },
                        onSelect: (newCity) => {
                          formConfig.setValue("city", newCity, {
                            shouldValidate: true,
                            shouldTouch: true,
                          });
                        },
                        focusAfterSelect: "city",
                      }}
                    />
                  </FormField>
                  <FormField>
                    <InputText
                      name="city"
                      required
                      label="Ville"
                      placeholder="Paris"
                    />
                  </FormField>
                </FormRow>
                <FormRow>
                  <FormField className="address-info__form-gap">
                    <InputText
                      name="mobilePhone"
                      required
                      label="Téléphone mobile (1)"
                    />
                    <InfoCard>
                      <div>
                        <p>
                          Il est à noter que ce numéro vous servira pour la
                          signature électronique du contrat.
                        </p>
                      </div>
                    </InfoCard>
                  </FormField>
                  <FormField>
                    <InputText name="fixedPhone" label="Téléphone fixe (1)" />
                  </FormField>
                </FormRow>
                <FormRow>
                  <FormField className="address-info__form-gap">
                    <InputText name="email" label="Email" required />
                    <InfoCard>
                      <div>
                        <p>
                          Un email récapitulatif vous sera envoyé à l&apos;issue
                          de cette étape. Depuis cet e-mail, vous pourrez à tout
                          moment, retrouver vos informations personnelles, votre
                          tarif et souscrire.
                        </p>
                      </div>
                    </InfoCard>
                  </FormField>
                </FormRow>
                <FormRow>
                  <InputCheckbox
                    name="accept"
                    className="address-info__form-checkbox"
                    label="J'accepte que les services de la mutuelle me rappellent au sujet de mon devis."
                  />
                </FormRow>
                <FormRow>
                  <InputCheckbox
                    name="consent"
                    className="address-info__form-checkbox"
                    required
                    label={
                      <>
                        J&apos;accepte de recevoir chacun des documents sous
                        format dématérialisé, le processus de souscription étant
                        entièrement digitalisé
                        <PopperButton
                          popperPlacement="top"
                          openOnHover
                          withArrow
                          renderButton={(props) => (
                            <InfoBulleButton size="large" {...props}>
                              {" "}
                            </InfoBulleButton>
                          )}
                          renderPopperContent={({ setVisible }) => (
                            <InfoBulle
                              className="address-info__info-bulle"
                              onCancel={() => setVisible(false)}
                            >
                              Si vous souhaitez souscrire autrement qu&apos;en
                              ligne, vous pouvez procéder à une adhésion papier
                              en contactant les services de la mutuelle.
                            </InfoBulle>
                          )}
                        />
                      </>
                    }
                  />
                </FormRow>
                <FormAction>
                  <Button
                    type="submit"
                    disabled={
                      !formConfig.formState.isValid ||
                      formConfig.formState.isSubmitting ||
                      formConfig.formState.isSubmitSuccessful
                    }
                  >
                    Suivant
                  </Button>
                </FormAction>
                <p className="address-info__consents">
                  <small>(1)</small> Si vous ne souhaitez pas faire l&apos;objet
                  de prospection commerciale par voie téléphonique, vous pouvez
                  vous inscrire gratuitement sur la liste d&apos;opposition au
                  démarchage téléphonique BLOCTEL sur le site internet{" "}
                  <a
                    href="https://www.bloctel.gouv.fr"
                    target="_blank"
                    rel="noreferrer"
                  >
                    www.bloctel.gouv.fr
                  </a>
                  . <br />
                  Les consommateur(rices) inscrit(es) sur cette liste ne peuvent
                  pas être démarché(es) sauf lorsqu&apos;il s&apos;agit de
                  sollicitations du professionnel intervenant dans le cadre de
                  l&apos;exécution d&apos;un contrat en cours et ayant un
                  rapport avec l&apos;objet de ce contrat.
                </p>
              </FormGrid>
            </Form>
          </div>
        </div>
      </div>
    </>
  );
}
